import { AnalyticsContext } from '@carvana/eventable-clickstream';
import React, {
  SetStateAction,
  useCallback,
  useContext,
  useState
} from 'react';
import styled from 'styled-components';
import ModalContext from '|/app/modals';
import { Summary, cancelOrder } from '|/app/services';
import * as sc from '|/app/steps/navigation-rail/cancel-order-modal/cancel-order-modal.styles';
import Radio from '|/components/base/radio';
import AppFrameworkContext, {
  Button as BaseButton
} from '|/helpers/app-framework';
import useOrderSummaryVm from '|/helpers/use-order-summary';
import CheckCircleSvg from '|/icons/check-circle.svg';

const CheckCircle = styled(CheckCircleSvg)`
  width: 50px;
  height: 50px;

  & > path {
    fill: var(--colors-primary-green-2);
  }
`;

const Button = styled(BaseButton)`
  width: 251px;
`;

const formatMoney = (number: number) =>
  number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  });

const confirmDefaultProps = {
  setIsCancelled: (() => {}) as React.Dispatch<SetStateAction<boolean>>,
  shippingFee: null as null | number,
  vehicleId: null as null | number,
  isShippingFeeNonRefundable: false
};

const CancelOrderConfirm = ({
  setIsCancelled,
  shippingFee,
  vehicleId,
  isShippingFeeNonRefundable
} = confirmDefaultProps) => {
  const [selected, setSelected] = useState('');
  const [otherText, setOtherText] = useState('');
  const { trackEvent } = useContext(AnalyticsContext);
  const { setCancelOrderModalOpen } = useContext(ModalContext);
  const { submit } = useContext(AppFrameworkContext);

  const shippingFeeFormatted = formatMoney(shippingFee ?? 0);

  const confirmCancelOrder = () => {
    trackEvent('Cancel option selected', {
      cancelOption: selected,
      otherText: selected === 'Other' ? otherText : '',
      vehicleId
    });
    trackEvent('v2_opd_cancel_order_button_tap');
    trackEvent('pp_clicked_cancel_purchase');

    submit(
      cancelOrder({ reasons: ['CustomerSelfCancelled'] }).then(() =>
        setIsCancelled(true)
      ),
      null,
      false
    );
  };

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) =>
      setOtherText(event.target.value),
    [setOtherText]
  );

  const Options = [
    'Decided not to buy a car right now',
    'Switching to another Carvana car',
    'No longer fits my budget',
    'Delivery Date has been delayed',
    'Poor customer experience',
    'Found a car elsewhere',
    'Other'
  ];

  return (
    <>
      <sc.AlertIcon />
      <sc.BoldText>Are you sure you want to cancel your order?</sc.BoldText>
      <sc.BodyText>
        This vehicle will be immediately available for other buyers and cannot
        be undone.
      </sc.BodyText>
      {shippingFee > 0 && isShippingFeeNonRefundable ? (
        <sc.BodyText>
          Your <b>{shippingFeeFormatted}</b> shipping fee may not be eligible
          for a refund.
        </sc.BodyText>
      ) : null}
      <sc.BodyText>Select a reason:</sc.BodyText>
      <sc.OptionsWrapper>
        {Options.map(option => {
          return (
            <sc.Option key={option}>
              <Radio
                value={selected === option}
                checked={selected === option}
                text={option}
                onChange={() => setSelected(option)}
                useNormalFontWeight={true}
              />
            </sc.Option>
          );
        })}

        {selected === 'Other' && (
          <sc.OptionText>
            <form>
              <sc.TextAreaStyle
                value={otherText}
                onChange={handleChange}
                rows={4}
                placeholder="Add a message here"
                maxLength={250}
              />
            </form>
          </sc.OptionText>
        )}
      </sc.OptionsWrapper>
      <sc.ButtonWrapper>
        <Button
          onClick={confirmCancelOrder}
          disabled={selected.length === 0}
          hierarchyType="secondary"
          color="blue"
          size="large"
          icon="none"
          text="Yes, cancel"
          data-analytics-id="pp_cancel_modal_yes"
        />
        <Button
          hierarchyType="primary"
          color="blue"
          size="large"
          icon="none"
          text="No, go back"
          onClick={() => setCancelOrderModalOpen(false)}
          data-analytics-id="pp_cancel_modal_no"
        />
      </sc.ButtonWrapper>
    </>
  );
};

const redirectToInventory = () => {
  window.location.href = `${process.env.BASE_URL}/cars`;
};

const CancelOrderSuccess = () => {
  return (
    <>
      <CheckCircle size="50px" />
      <sc.BoldText>Your order has been cancelled.</sc.BoldText>
      <sc.BodyText>
        Sorry this one didn&apos;t work out. If you&apos;re still looking for a
        car, check out our other vehicles.
      </sc.BodyText>
      <sc.ButtonWrapper>
        <Button
          hierarchyType="primary"
          color="blue"
          size="large"
          icon="none"
          onClick={() => redirectToInventory()}
          text="Search Vehicles"
          data-analytics-id="pp_cancel_modal_search"
        >
          Search vehicles
        </Button>
      </sc.ButtonWrapper>
    </>
  );
};

const defaultProps = {
  summary: null as null | Summary
};

const CancelOrderModal = ({ summary } = defaultProps) => {
  const {
    shippingFee,
    vehicleId,
    isShippingFeeNonRefundable
  } = useOrderSummaryVm(summary || {});
  const [isCancelled, setIsCancelled] = useState(false);

  return (
    <sc.ContentWrapper>
      {isCancelled ? (
        <CancelOrderSuccess />
      ) : (
        <CancelOrderConfirm
          setIsCancelled={setIsCancelled}
          shippingFee={shippingFee}
          vehicleId={vehicleId}
          isShippingFeeNonRefundable={isShippingFeeNonRefundable}
        />
      )}
    </sc.ContentWrapper>
  );
};

export default CancelOrderModal;
